import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Globe, List, Network, Plus, RefreshCw, Search, X } from 'lucide-react';
import { apiClient } from '../api';
import { primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, errMessage } from '../components/UI/ink';
import {
  QUICK, ago, hasAgent, isSilent, quickMatch, reachesInternet, statusOf, toAsset, typeLabel, unknownAddresses,
} from '../components/NetworkMap/model';
import TopologyMap from '../components/NetworkMap/TopologyMap';
import { gatewayFor } from '../components/NetworkMap/topology';
import AssetList from '../components/NetworkMap/AssetList';
import Inspector, { DEVICE_TYPES } from '../components/NetworkMap/Inspector';
import ReviewTray from '../components/NetworkMap/ReviewTray';
import AddAssetDrawer from '../components/NetworkMap/AddAssetDrawer';

// Network Map, triage-first: summary tiles that filter the map, the map (or
// list) with a review tray under it, and an inspector that explains the
// selected asset's risk. Filtering dims map nodes instead of removing them so
// the layout doesn't jump.

const seg = (on) => ({
  background: on ? 'var(--surf-raised)' : 'transparent',
  borderColor: on ? 'var(--surf-line)' : 'transparent',
  color: on ? 'var(--ink-text)' : 'var(--ink-muted)',
});

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
const names = (list) => (list.length <= 2 ? list.join(', ') : `${list.slice(0, 2).join(', ')} and ${list.length - 2} more`);

const Tile = ({ id, on, onToggle, mark, title, count, unit, detail }) => (
  <button
    type="button"
    onClick={() => onToggle(id)}
    aria-pressed={on}
    className="text-left flex flex-col gap-1.5 px-[18px] py-4 rounded-xl border"
    style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}
  >
    <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.1em]" style={{ color: 'var(--ink-muted)' }}>{mark}{title}</span>
    <span className="flex items-baseline gap-2">
      <span className="font-heading text-[28px] font-semibold leading-tight">{count}</span>
      <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>{unit}</span>
    </span>
    <span className="text-[12.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{detail}</span>
  </button>
);

const NetworkMap = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [notice, setNotice] = useState(null);
  const [search, setSearch] = useState('');
  const [quick, setQuick] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addIp, setAddIp] = useState('');
  const [links, setLinks] = useState([]);
  const [linkHours, setLinkHours] = useState(24);
  const [focus, setFocus] = useState(null);
  const [topo, setTopo] = useState(null);
  const searchRef = useRef(null);

  const view = searchParams.get('view') === 'list' ? 'list' : 'map';
  const selectedId = searchParams.get('asset');
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const select = (id) => setParam('asset', id);

  const load = useCallback(async () => {
    try {
      const [serversRes, riskRes] = await Promise.all([
        apiClient.get('/api/servers'),
        apiClient.get('/api/dashboard/risk').catch(() => null),
      ]);
      const riskMap = new Map((riskRes?.data?.servers || []).map(s => [s.id, s]));
      const rows = (serversRes.data.servers || [])
        .filter(s => !['IGNORED', 'ARCHIVED'].includes(s.lifecycle_state));
      setAssets(rows.map(s => toAsset(s, riskMap)));
      setUpdatedAt(new Date());
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Failed to load assets.') });
    } finally {
      setLoading(false);
    }
  }, []);

  // Who talks to whom (tiles, review tray, inspector) over the chosen window.
  const windowHours = linkHours || 24;
  const loadLinks = useCallback(async () => {
    const res = await apiClient.get(`/api/network/edges?hours=${windowHours}`).catch(() => null);
    setLinks(res?.data?.edges || []);
  }, [windowHours]);

  // How each device reaches the network (access point or cable, and gateway).
  const loadTopo = useCallback(async () => {
    const res = await apiClient.get('/api/network/topology').catch(() => null);
    setTopo(res?.data || null);
  }, []);

  useEffect(() => { load(); loadTopo(); }, [load, loadTopo]);
  useEffect(() => { loadLinks(); }, [loadLinks]);
  const refresh = () => { setLoading(true); load(); loadLinks(); loadTopo(); };

  const updateAp = async (key, body) => {
    try {
      await apiClient.patch(`/api/network/aps/${encodeURIComponent(key)}`, body);
      setNotice({ kind: 'ok', text: body.status === 'known' ? 'Access point accepted as yours.' : 'Access point updated.' });
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Failed to update the access point.') });
    }
    await loadTopo();
  };

  // "/" jumps to the search box, as the hint in it says.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const byId = useMemo(() => Object.fromEntries(assets.map(a => [a.id, a])), [assets]);
  const selected = selectedId ? byId[selectedId] || null : null;
  const selectedNetwork = useMemo(() => {
    if (!selectedId || !topo) return null;
    const link = topo.links?.find(l => l.server_id === selectedId) || null;
    const snmp = topo.snmp_devices?.find(d => d.asset_id === selectedId) || null;
    const placement = topo.placements?.find(p => p.server_id === selectedId) || null;
    if (!link && !snmp && !placement) return null;
    return {
      link,
      ap: link ? topo.access_points?.find(ap => ap.key === link.ap_key) || null : null,
      gateway: gatewayFor(topo, byId[selectedId]?.location),
      snmp,
      placement,
      via: placement ? topo.snmp_devices?.find(d => d.ip === placement.device_ip && (d.location || '') === (placement.device_location || '')) || null : null,
    };
  }, [selectedId, topo, byId]);
  const internetIds = useMemo(() => reachesInternet(links), [links]);
  const unknown = useMemo(() => unknownAddresses(links), [links]);

  const q = search.trim().toLowerCase();
  const searchHit = useCallback(a => !q
    || a.host.toLowerCase().includes(q) || a.ip.includes(q) || (a.hostname || '').toLowerCase().includes(q)
    || a.ports.some(p => String(p.port) === q),
  [q]);
  const matches = useCallback(a => (!quick || quickMatch(quick, a, internetIds)) && searchHit(a), [quick, internetIds, searchHit]);

  // Enter in the search box selects the best hit and moves the map to it.
  const jump = (e) => {
    e.preventDefault();
    if (!q) return;
    const hits = assets.filter(searchHit);
    const best = hits.find(a => a.host.toLowerCase() === q || a.ip === q) || hits[0];
    if (!best) { setNotice({ kind: 'error', text: `Nothing matches “${search.trim()}”.` }); return; }
    select(best.id);
    setFocus({ id: best.id, at: Date.now() });
  };

  // Tile numbers.
  const t = useMemo(() => {
    const crit = assets.filter(a => a.band === 'crit').sort((x, y) => y.risk - x.risk);
    const silent = assets.filter(isSilent);
    const offline = silent.filter(a => statusOf(a).key === 'offline');
    const reach = assets.filter(a => internetIds.has(a.id) || a.internetFacing);
    const peers = links.filter(e => e.kind === 'internet').reduce((n, e) => n + e.peers, 0);
    const discovered = assets.filter(a => a.discovered);
    const finders = [...new Set(discovered.map(a => byId[a.discoveredBy]?.host).filter(Boolean))];
    return { crit, silent, offline, reach, peers, discovered, finders };
  }, [assets, internetIds, links, byId]);
  const latest = t.silent.slice().sort((x, y) => (y.last_seen || '').localeCompare(x.last_seen || ''))[0];
  const windowLabel = windowHours === 168 ? '7 days' : windowHours === 1 ? 'hour' : `${windowHours} h`;

  const toggleQuick = (k) => setQuick(cur => (cur === k ? null : k));

  const onChanged = (lifecycle, a) => {
    setNotice({ kind: 'ok', text: lifecycle === 'IGNORED' ? `${a.host} dismissed.` : `${a.host} registered.` });
    if (lifecycle === 'IGNORED') select(null);
    load();
  };

  // Accept (with what discovery already knows) or dismiss one or more devices.
  const review = async (list, lifecycle) => {
    const items = Array.isArray(list) ? list : [list];
    try {
      await Promise.all(items.map(a => apiClient.put(`/api/servers/${a.id}`, lifecycle === 'IGNORED'
        ? { lifecycle_state: 'IGNORED' }
        : {
            lifecycle_state: 'REGISTERED',
            hostname: a.unnamed ? a.ip : a.hostname,
            device_type: DEVICE_TYPES.some(([v]) => v === a.deviceType) ? a.deviceType : 'unknown',
            location: a.location || null,
            criticality_score: a.criticality,
            is_internet_facing: a.internetFacing ? 1 : 0,
          })));
      const what = items.length === 1 ? items[0].host : plural(items.length, 'device');
      setNotice({ kind: 'ok', text: `${what} ${lifecycle === 'IGNORED' ? 'dismissed' : 'registered'}.` });
      if (lifecycle === 'IGNORED' && items.some(a => a.id === selectedId)) select(null);
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, lifecycle === 'IGNORED' ? 'Failed to dismiss.' : 'Failed to accept.') });
    }
    await load();
  };

  const onDelete = async (a) => {
    try {
      await apiClient.delete(`/api/servers/${a.id}`);
      select(null);
      setNotice({ kind: 'ok', text: `${a.host} deleted.` });
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: errMessage(err, 'Failed to delete asset.') });
    }
  };

  const openAdd = (ip = '') => { setAddIp(ip); setAddOpen(true); };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Name', 'IP address', 'Type', 'Risk score', 'Criticality', 'Status', 'Open findings', 'Open CVEs', 'Open ports', 'Location', 'Discovered by', 'Last seen'];
    const rows = assets.filter(matches).map(a => [
      a.host, a.ip, typeLabel(a.deviceType), a.risk ?? '', a.criticality, statusOf(a).label,
      a.findings?.total ?? 0, a.vulns, a.ports.map(p => p.port).join(' '), a.location,
      a.discoveredBy ? byId[a.discoveredBy]?.host || a.discoveredBy : '', a.last_seen || '',
    ].map(esc).join(','));
    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dragoneye_assets_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const withAgents = assets.filter(hasAgent).length;
  const sq = 'w-2 h-2 rounded-[2px] shrink-0';

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-5" style={{ color: 'var(--ink-text)' }}>
      {/* Title row */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>ASSETS &amp; DEFENSE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">Network Map</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>
            {loading && !updatedAt ? 'Loading…' : `${plural(assets.length, 'asset')} · ${withAgents} with agents${updatedAt ? ` · updated ${updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <form role="search" onSubmit={jump} className="w-full sm:w-[300px] h-11 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            <Search className="w-[15px] h-[15px] shrink-0" style={{ color: 'var(--ink-faint)' }} strokeWidth={1.8} />
            <input
              ref={searchRef}
              type="search"
              aria-label="Find an asset by name, IP or port; Enter jumps to it"
              placeholder="Find asset, IP or port"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="flex-grow min-w-0 h-[40px] bg-transparent border-0 outline-none text-[13.5px]"
              style={{ color: 'var(--ink-text)' }}
            />
            <kbd className="font-mono text-[11px] px-1.5 py-px rounded-[5px] border" style={{ color: 'var(--ink-faint)', borderColor: 'var(--surf-line-strong)' }}>/</kbd>
          </form>
          <div role="group" aria-label="View" className="flex gap-1 p-1 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
            {[['map', 'Map', Network], ['list', 'List', List]].map(([id, label, Icon]) => (
              <button key={id} type="button" onClick={() => setParam('view', id === 'map' ? null : id)} aria-pressed={view === id} className="h-[34px] flex items-center gap-[7px] px-3 rounded-[7px] border text-[13px]" style={seg(view === id)}>
                <Icon className="w-[15px] h-[15px]" strokeWidth={1.8} />{label}
              </button>
            ))}
          </div>
          <button type="button" onClick={refresh} aria-label="Refresh" title="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />
          </button>
          <button type="button" onClick={exportCsv} className={secondaryBtn} style={secondaryBtnStyle}>
            <Download className="w-4 h-4" strokeWidth={1.8} />Export
          </button>
          <button type="button" onClick={() => openAdd()} className={primaryBtn} style={primaryBtnStyle}>
            <Plus className="w-4 h-4" strokeWidth={2} />Add asset
          </button>
        </div>
      </div>

      {/* Summary tiles: each one filters the map and list */}
      <div role="group" aria-label="Quick filters" className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        <Tile
          id={QUICK[0]} on={quick === QUICK[0]} onToggle={toggleQuick}
          mark={<span className={sq} style={{ background: 'var(--sev-crit-fg)' }} />}
          title="CRITICAL RISK" count={t.crit.length} unit={t.crit.length === 1 ? 'asset' : 'assets'}
          detail={t.crit.length === 1
            ? `${t.crit[0].host} · risk ${Math.round(t.crit[0].risk)} · ${plural(t.crit[0].findings?.total || 0, 'open finding')}`
            : t.crit.length ? names(t.crit.map(a => a.host)) : 'Nothing scores above 75'}
        />
        <Tile
          id={QUICK[1]} on={quick === QUICK[1]} onToggle={toggleQuick}
          mark={<span className="w-2 h-2 rounded-full shrink-0" style={{ background: 'var(--sev-med-fg)' }} />}
          title="NOT REPORTING" count={t.silent.length - t.offline.length} unit={`late · ${t.offline.length} offline`}
          detail={latest ? `${latest.host} · last heartbeat ${latest.last_seen ? `${ago(latest.last_seen)} ago` : 'never'}` : 'Every agent reported in the last 15 min'}
        />
        <Tile
          id={QUICK[2]} on={quick === QUICK[2]} onToggle={toggleQuick}
          mark={<Globe className="w-3 h-3 shrink-0" strokeWidth={2} style={{ color: 'var(--accent)' }} />}
          title="REACH THE INTERNET" count={t.reach.length} unit={`${t.reach.length === 1 ? 'asset' : 'assets'} · ${assets.filter(a => a.internetFacing).length} internet-facing`}
          detail={`${plural(t.peers, 'public address', 'public addresses')} contacted in the last ${windowLabel}`}
        />
        <Tile
          id={QUICK[3]} on={quick === QUICK[3]} onToggle={toggleQuick}
          mark={<span className="w-2 h-2 rounded-full box-border shrink-0 border-[1.5px] border-dashed" style={{ borderColor: 'var(--ink-muted)' }} />}
          title="WAITING FOR REVIEW" count={t.discovered.length + unknown.length} unit="devices"
          detail={[t.discovered.length && `${t.discovered.length} discovered${t.finders.length ? ` by ${names(t.finders)}` : ''}`, unknown.length && plural(unknown.length, 'unknown address', 'unknown addresses')].filter(Boolean).join(' · ') || 'Nothing to review'}
        />
      </div>

      {(quick || q) && (
        <div className="flex items-center gap-3 -mt-1 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
          <span>Showing {assets.filter(matches).length} of {assets.length}</span>
          <button type="button" onClick={() => { setQuick(null); setSearch(''); }} className="underline-offset-2 hover:underline">Clear filters</button>
        </div>
      )}

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-start justify-between gap-3 px-4 py-3 rounded-[10px] border text-[13px]" style={{
          borderColor: notice.kind === 'error' ? 'var(--sev-crit-fg)' : 'var(--sev-good-fg)',
          background: notice.kind === 'error' ? 'var(--sev-crit-bg)' : 'var(--sev-good-bg)',
        }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="shrink-0" style={{ color: 'var(--ink-muted)' }}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
        <div className="flex flex-col gap-4 min-w-0">
          <div className="h-[780px]">
            {view === 'map' ? (
              <TopologyMap
                assets={assets}
                topo={topo}
                matches={matches}
                selectedId={selectedId}
                onSelect={select}
                linkHours={windowHours}
                onLinkHours={setLinkHours}
                focus={focus}
                onUpdateAp={updateAp}
                internetPeers={t.peers}
              />
            ) : (
              <AssetList assets={assets.filter(matches)} byId={byId} selectedId={selectedId} onSelect={select} />
            )}
          </div>
          <ReviewTray
            discovered={t.discovered}
            unknown={unknown}
            byId={byId}
            onSelect={select}
            onReview={review}
            onAddAddress={openAdd}
          />
        </div>
        <div className="xl:sticky xl:top-6 xl:h-[min(880px,calc(100vh-48px))]">
          <Inspector
            asset={selected}
            assets={assets}
            byId={byId}
            links={links}
            network={selectedNetwork}
            onSelect={select}
            onChanged={onChanged}
            onDelete={onDelete}
          />
        </div>
      </div>

      <AddAssetDrawer
        open={addOpen}
        initialIp={addIp}
        onClose={() => setAddOpen(false)}
        onAdded={async () => { setNotice({ kind: 'ok', text: 'Asset added.' }); await Promise.all([load(), loadLinks(), loadTopo()]); }}
      />
    </div>
  );
};

export default NetworkMap;
